import {
	Box,
	Group,
	Image,
	Modal,
	Stack,
	UnstyledButton,
	useMantineTheme,
} from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { Button, Icon, Title } from '@repo/foundations';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { Link } from 'react-router';
import { EDUCATION_MODALS } from './constants';
import { useEducationModalDismiss } from './useEducationModalDismiss';

export function EducationModal({
	modalKey,
	forceShow = false,
}: {
	modalKey: keyof typeof EDUCATION_MODALS;
	forceShow?: boolean;
}) {
	const {
		title,
		imageUrl,
		learnMoreLink,
		children,
		primaryActionUrl,
		primaryActionLabel,
		width,
		expiresAt,
	} = EDUCATION_MODALS[modalKey];
	const [isPlaywrightTesting] = useLocalStorage({
		key: 'PLAYWRIGHT_TESTING',
		defaultValue: false,
	});

	const theme = useMantineTheme();

	const { setAsSeen, dismissedModals } = useEducationModalDismiss();

	// Prevent the modal from being shown again if the user has dismissed it locally
	const [locallyDismissed, setLocallyDismissed] = useState(false);

	const previouslyShown = !!dismissedModals?.[modalKey];
	const isExpired = dayjs().isAfter(expiresAt);

	const canShowModal =
		(!previouslyShown && !isExpired && !isPlaywrightTesting) || forceShow;

	const opened = canShowModal && !locallyDismissed;

	const handleClose = useCallback(() => {
		setLocallyDismissed(true);
		setAsSeen(modalKey);
	}, [modalKey, setAsSeen]);

	return (
		<Modal
			opened={opened}
			onClose={handleClose}
			closeOnClickOutside={true}
			withCloseButton={false}
			size={width}
			radius="lg"
			styles={{
				body: {
					position: 'relative',
					padding: 0,
				},
			}}
		>
			<Box
				sx={{
					position: 'absolute',
					top: theme.spacing.md,
					right: theme.spacing.md,
					zIndex: 1000,
				}}
			>
				<UnstyledButton
					onClick={handleClose}
					aria-label="Close modal"
					sx={{
						'&:focus': {
							outline: 'none',
						},
						'&:focus-visible': {
							outline: 'none',
						},
					}}
				>
					<Icon name="x" />
				</UnstyledButton>
			</Box>
			<Image src={imageUrl} alt={title} />
			<Stack p="xl" spacing="xs">
				<Title order={1}>{title}</Title>
				{children}
			</Stack>
			<Group
				p="md"
				sx={{
					borderTop: `1px solid ${theme.other.getColor('border/primary/default')}`,
					justifyContent: 'flex-end',
				}}
			>
				<Group spacing="xs" noWrap>
					{learnMoreLink && (
						<Button
							component={Link}
							target="_blank"
							leftIconName="externalLink"
							to={learnMoreLink}
							onClick={handleClose}
						>
							Learn more
						</Button>
					)}

					<Button
						component={Link}
						data-testid="education-learn-more-button"
						variant="primary"
						to={primaryActionUrl}
						onClick={handleClose}
					>
						{primaryActionLabel}
					</Button>
				</Group>
			</Group>
		</Modal>
	);
}
